import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  X,
  Plus,
  Github,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { useTags } from "@/hooks/useAgents";
import { apiService, type ApiAgent, ApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { transformToApiAgent } from "@/lib/data-transform";
import { isValidGitHubUrl, normalizeGitHubUrl, isValidUrl } from "@/lib/utils";

const agentSchema = z.object({
  title: z
    .string()
    .min(5, "Title must be at least 5 characters")
    .max(100, "Title must be less than 100 characters"),
  description: z
    .string()
    .min(20, "Description must be at least 20 characters")
    .max(500, "Description must be less than 500 characters"),
  tags: z
    .array(z.string())
    .min(1, "At least one tag is required")
    .max(10, "Maximum 10 tags allowed"),
  githubUrl: z
    .string()
    .url("Invalid GitHub URL")
    .optional()
    .or(z.literal(""))
    .refine(
      (url) => !url || isValidGitHubUrl(url),
      "Please enter a valid GitHub repository URL in format: https://github.com/owner/repo",
    ),
  websiteUrl: z
    .string()
    .url("Invalid website URL")
    .optional()
    .or(z.literal(""))
    .refine(
      (url) => !url || isValidUrl(url),
      "Please enter a valid website URL",
    ),
  markdownContent: z.string().optional(),
});

type AgentFormSchema = z.infer<typeof agentSchema>;

const SubmitAgentManual: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [urlValidation, setUrlValidation] = useState<{
    githubUrl?: {
      isValid: boolean;
      message?: string;
      redirecting?: boolean;
      agentId?: string;
    };
    websiteUrl?: { isValid: boolean; message?: string };
  }>({});

  // Load available tags
  const { tags: availableTags, loading: tagsLoading } = useTags();

  // Enhanced URL validation with improved duplicate checking
  const validateUrl = async (type: "githubUrl" | "websiteUrl", url: string) => {
    if (!url) {
      setUrlValidation((prev) => ({ ...prev, [type]: undefined }));
      return;
    }

    setUrlValidation((prev) => ({
      ...prev,
      [type]: { isValid: true, message: "Checking..." },
    }));

    try {
      // Validate URL format first
      if (type === "githubUrl" && !isValidGitHubUrl(url)) {
        setUrlValidation((prev) => ({
          ...prev,
          [type]: {
            isValid: false,
            message:
              "Invalid GitHub URL format. Use: https://github.com/owner/repo",
          },
        }));
        return;
      }

      if (type === "websiteUrl" && !isValidUrl(url)) {
        setUrlValidation((prev) => ({
          ...prev,
          [type]: {
            isValid: false,
            message: "Invalid website URL format",
          },
        }));
        return;
      }

      // Check for duplicates by searching existing agents
      const response = await apiService.getAgents(1000);

      const existingAgent = response.agents.find((agent: ApiAgent) => {
        if (type === "githubUrl" && agent.githubUrl) {
          return (
            normalizeGitHubUrl(agent.githubUrl) === normalizeGitHubUrl(url)
          );
        }
        if (type === "websiteUrl" && agent.websiteUrl) {
          return agent.websiteUrl.toLowerCase() === url.toLowerCase();
        }
        return false;
      });

      if (existingAgent) {
        if (type === "githubUrl") {
          // For GitHub URLs, trigger auto-redirect
          setUrlValidation((prev) => ({
            ...prev,
            [type]: {
              isValid: false,
              message: `Duplicate detected! Redirecting to "${existingAgent.title}"...`,
              redirecting: true,
              agentId: existingAgent.id,
            },
          }));

          // Auto-redirect after showing the message
          setTimeout(() => {
            navigate(`/agent/${existingAgent.id}`);
          }, 2000);
        } else {
          // For website URLs, just show the duplicate message
          setUrlValidation((prev) => ({
            ...prev,
            [type]: {
              isValid: false,
              message: `This website URL is already used by "${existingAgent.title}"`,
            },
          }));
        }
      } else {
        setUrlValidation((prev) => ({
          ...prev,
          [type]: {
            isValid: true,
            message: `${type === "githubUrl" ? "GitHub" : "Website"} URL is available ✓`,
          },
        }));
      }
    } catch (error) {
      logger.error("Error validating URL:", error);
      setUrlValidation((prev) => ({
        ...prev,
        [type]: {
          isValid: true,
          message: "Could not validate URL",
        },
      }));
    }
  };

  // Debounce URL validation
  const debouncedValidateUrl = React.useCallback(
    (type: "githubUrl" | "websiteUrl", url: string) => {
      const timeoutId = setTimeout(() => validateUrl(type, url), 1000);
      return () => clearTimeout(timeoutId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate], // validateUrl is stable, navigate added to deps
  );

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AgentFormSchema>({
    resolver: zodResolver(agentSchema),
    defaultValues: {
      tags: [],
    },
  });

  React.useEffect(() => {
    setValue("tags", selectedTags);
  }, [selectedTags, setValue]);

  const addTag = (tag: string) => {
    if (tag && !selectedTags.includes(tag) && selectedTags.length < 10) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const removeTag = (tagToRemove: string) => {
    setSelectedTags(selectedTags.filter((tag) => tag !== tagToRemove));
  };

  const addCustomTag = () => {
    if (customTag.trim()) {
      addTag(customTag.trim().toLowerCase());
      setCustomTag("");
    }
  };

  const onSubmit = async (data: AgentFormSchema) => {
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      // Transform form data to API format with anonymous submission
      const apiData = transformToApiAgent({
        ...data,
        authorName: "Anonymous",
        authorEmail: "",
      });

      // Submit to API
      const createdAgent = await apiService.createAgent(apiData);

      // Show success state
      setSubmitSuccess(true);

      // Reset form
      reset();
      setSelectedTags([]);

      // Redirect to agent detail page after a short delay
      setTimeout(() => {
        navigate(`/agent/${createdAgent.id}`);
      }, 2000);
    } catch (error) {
      logger.error("Error submitting agent:", error);

      // Handle URL conflict errors with detailed feedback
      if (error instanceof Error) {
        // Try to parse API error response for conflict details
        let errorMessage = error.message;

        try {
          // Check if error has response data with conflict details
          if (error instanceof ApiError && error.response) {
            const errorResponse = error.response as {
              details?: string[];
              message?: string;
            };
            if (errorResponse.details && Array.isArray(errorResponse.details)) {
              errorMessage = `URL conflicts detected:\n${errorResponse.details.join("\n")}`;
            } else if (errorResponse.message) {
              errorMessage = errorResponse.message;
            }
          }
        } catch (parseError) {
          // Fallback to original error message
          logger.warn("Could not parse error response:", parseError);
        }

        setSubmitError(errorMessage);
      } else {
        setSubmitError("Failed to submit agent. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-3xl lg:text-4xl font-bold mb-4">
              Submit Your Infrastructure Agent (Manual)
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Manually provide all details for your infrastructure automation
              solution. Use this form when you need full control over the
              submission process.
            </p>
          </div>

          <div className="rounded-lg border bg-card">
            <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-8">
              {/* Basic Information */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Basic Information</h2>

                <div>
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium mb-2"
                  >
                    Agent Title *
                  </label>
                  <input
                    {...register("title")}
                    type="text"
                    id="title"
                    placeholder="e.g., Kubernetes Cluster Setup Agent"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium mb-2"
                  >
                    Description *
                  </label>
                  <textarea
                    {...register("description")}
                    id="description"
                    rows={4}
                    placeholder="Describe what your infrastructure agent does, what problems it solves, and its key features..."
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                  />
                  {errors.description && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Tags * (Select relevant technologies)
                  </label>

                  {/* Popular Tags */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
                    {tagsLoading ? (
                      <div className="col-span-full text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto"></div>
                      </div>
                    ) : (
                      availableTags.slice(0, 18).map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => addTag(tag.name)}
                          disabled={selectedTags.includes(tag.name)}
                          className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                            selectedTags.includes(tag.name)
                              ? "bg-primary text-primary-foreground cursor-not-allowed"
                              : "border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                          }`}
                        >
                          {tag.name}
                        </button>
                      ))
                    )}
                  </div>

                  {/* Custom Tag Input */}
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={customTag}
                      onChange={(e) => setCustomTag(e.target.value)}
                      placeholder="Add custom tag"
                      className="flex h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), addCustomTag())
                      }
                    />
                    <button
                      type="button"
                      onClick={addCustomTag}
                      disabled={!customTag.trim()}
                      className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Selected Tags */}
                  {selectedTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedTags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-sm font-medium text-secondary-foreground"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-2 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {errors.tags && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.tags.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Links */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Links & Resources</h2>

                <div>
                  <label
                    htmlFor="githubUrl"
                    className="block text-sm font-medium mb-2"
                  >
                    GitHub Repository URL
                  </label>
                  <div className="relative">
                    <Github className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      {...register("githubUrl", {
                        onChange: (e) => {
                          const url = e.target.value;
                          if (url) {
                            debouncedValidateUrl("githubUrl", url);
                          } else {
                            setUrlValidation((prev) => ({
                              ...prev,
                              githubUrl: undefined,
                            }));
                          }
                        },
                      })}
                      type="url"
                      id="githubUrl"
                      placeholder="https://github.com/username/repository"
                      className={`flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                        urlValidation.githubUrl?.isValid === false
                          ? "border-red-500"
                          : urlValidation.githubUrl?.isValid === true &&
                              urlValidation.githubUrl.message !== "Checking..."
                            ? "border-green-500"
                            : ""
                      }`}
                    />
                    {urlValidation.githubUrl && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {urlValidation.githubUrl.message === "Checking..." ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : urlValidation.githubUrl.isValid ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {errors.githubUrl && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.githubUrl.message}
                    </p>
                  )}
                  {urlValidation.githubUrl?.message && !errors.githubUrl && (
                    <p
                      className={`text-sm mt-1 ${urlValidation.githubUrl.isValid ? "text-green-600" : "text-red-600"}`}
                    >
                      {urlValidation.githubUrl.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="websiteUrl"
                    className="block text-sm font-medium mb-2"
                  >
                    Website or Documentation URL
                  </label>
                  <div className="relative">
                    <ExternalLink className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      {...register("websiteUrl", {
                        onChange: (e) => {
                          const url = e.target.value;
                          if (url) {
                            debouncedValidateUrl("websiteUrl", url);
                          } else {
                            setUrlValidation((prev) => ({
                              ...prev,
                              websiteUrl: undefined,
                            }));
                          }
                        },
                      })}
                      type="url"
                      id="websiteUrl"
                      placeholder="https://your-agent-docs.com"
                      className={`flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                        urlValidation.websiteUrl?.isValid === false
                          ? "border-red-500"
                          : urlValidation.websiteUrl?.isValid === true &&
                              urlValidation.websiteUrl.message !== "Checking..."
                            ? "border-green-500"
                            : ""
                      }`}
                    />
                    {urlValidation.websiteUrl && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {urlValidation.websiteUrl.message === "Checking..." ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : urlValidation.websiteUrl.isValid ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {errors.websiteUrl && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.websiteUrl.message}
                    </p>
                  )}
                  {urlValidation.websiteUrl?.message && !errors.websiteUrl && (
                    <p
                      className={`text-sm mt-1 ${urlValidation.websiteUrl.isValid ? "text-green-600" : "text-red-600"}`}
                    >
                      {urlValidation.websiteUrl.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Documentation */}
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="markdownContent"
                    className="block text-sm font-medium mb-2"
                  >
                    Additional Documentation (Markdown)
                  </label>
                  <textarea
                    {...register("markdownContent")}
                    id="markdownContent"
                    rows={8}
                    placeholder="Provide detailed documentation, usage examples, or configuration guides in Markdown format..."
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y font-mono"
                  />
                </div>
              </div>

              {/* Submission State */}
              {submitError && (
                <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/10 p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        Submission Failed
                      </p>
                      <div className="text-sm text-red-700 dark:text-red-300 mt-1">
                        {submitError.split("\n").map((line, index) => (
                          <p key={index} className={index > 0 ? "mt-1" : ""}>
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {submitSuccess && (
                <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/10 p-4">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        Agent Submitted Successfully!
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        Your infrastructure agent has been submitted and will be
                        reviewed shortly. Redirecting to agent page...
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || submitSuccess}
                  className="w-full inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-orange-500 to-red-500 px-8 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:from-orange-600 hover:to-red-600 hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-4 focus:ring-orange-200 dark:focus:ring-orange-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting && (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  )}
                  {isSubmitting
                    ? "Submitting..."
                    : submitSuccess
                      ? "Submitted!"
                      : "Submit Agent"}
                </button>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>
                  By submitting your infrastructure agent, you agree to our{" "}
                  <a href="/terms" className="underline hover:text-foreground">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    className="underline hover:text-foreground"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>

              {/* Link to Compact Form */}
              <div className="text-center pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate("/submit")}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Prefer AI-powered submission? Use the simple GitHub URL form
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitAgentManual;
